import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDataProvider, useGetList, type Identifier } from "ra-core";
import { useNavigate } from "react-router-dom";
import { AlertCircle, AlertTriangle, ExternalLink, User, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Sale } from "../types";

export interface DuplicateDetectionResult {
  isHardDuplicate: boolean;
  isSoftDuplicate: boolean;
  duplicateContact: any | null;
  reason: string;
}

export interface ContactDuplicateRadarProps {
  currentContactId?: Identifier;
  onDuplicateChange?: (result: DuplicateDetectionResult) => void;
}

export const ContactDuplicateRadar = ({
  currentContactId,
  onDuplicateChange,
}: ContactDuplicateRadarProps) => {
  const { control } = useFormContext();
  const firstName = useWatch({ control, name: "first_name" }) || "";
  const lastName = useWatch({ control, name: "last_name" }) || "";
  const emailJsonb = useWatch({ control, name: "email_jsonb" }) || [];
  const linkedinUrl = useWatch({ control, name: "linkedin_url" }) || "";

  const dataProvider = useDataProvider();
  const navigate = useNavigate();

  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  const [matchState, setMatchState] = useState<{
    contact: any | null;
    level: "hard" | "soft" | null;
    reason: string;
  }>({
    contact: null,
    level: null,
    reason: "",
  });

  useEffect(() => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const cleanLinkedin = linkedinUrl.trim().replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, "").replace(/\/.*$/, "");
    
    // Extract non-empty emails
    const validEmails: string[] = (Array.isArray(emailJsonb) ? emailJsonb : [])
      .map((item: any) => item?.email?.trim().toLowerCase())
      .filter((e: string | undefined): e is string => Boolean(e && e.includes("@")));

    if (!trimmedFirst && !trimmedLast && validEmails.length === 0 && !cleanLinkedin) {
      setMatchState({ contact: null, level: null, reason: "" });
      onDuplicateChange?.({
        isHardDuplicate: false,
        isSoftDuplicate: false,
        duplicateContact: null,
        reason: "",
      });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        let hardMatch: any = null;
        let hardReason = "";

        // 1. Check exact email matches in contacts_summary
        for (const email of validEmails) {
          if (email.length < 5) continue;
          const res = await dataProvider.getList("contacts_summary", {
            filter: { "email_fts@ilike": `%"${email}"%` },
            pagination: { page: 1, perPage: 5 },
            sort: { field: "id", order: "ASC" },
          });

          const matched = res.data.find((c) => c.id !== currentContactId);
          if (matched) {
            hardMatch = matched;
            hardReason = `A contact with email "${email}" is already registered.`;
            break;
          }
        }

        // 2. Check LinkedIn URL duplicate if no email match
        if (!hardMatch && cleanLinkedin.length >= 3) {
          const res = await dataProvider.getList("contacts_summary", {
            filter: { "linkedin_url@ilike": `%${cleanLinkedin}%` },
            pagination: { page: 1, perPage: 5 },
            sort: { field: "id", order: "ASC" },
          });

          const matched = res.data.find((c) => c.id !== currentContactId);
          if (matched) {
            hardMatch = matched;
            hardReason = "A contact with this LinkedIn profile already exists.";
          }
        }

        if (hardMatch) {
          setMatchState({
            contact: hardMatch,
            level: "hard",
            reason: hardReason,
          });
          onDuplicateChange?.({
            isHardDuplicate: true,
            isSoftDuplicate: false,
            duplicateContact: hardMatch,
            reason: hardReason,
          });
          return;
        }

        // 3. Check soft match: First Name + Last Name match
        if (trimmedFirst.length >= 2 && trimmedLast.length >= 2) {
          const res = await dataProvider.getList("contacts_summary", {
            filter: {
              "first_name@ilike": trimmedFirst,
              "last_name@ilike": trimmedLast,
            },
            pagination: { page: 1, perPage: 5 },
            sort: { field: "id", order: "ASC" },
          });

          const nameMatched = res.data.find((c) => c.id !== currentContactId);
          if (nameMatched) {
            const softReason = `A contact named "${trimmedFirst} ${trimmedLast}" already exists in the CRM.`;
            setMatchState({
              contact: nameMatched,
              level: "soft",
              reason: softReason,
            });
            onDuplicateChange?.({
              isHardDuplicate: false,
              isSoftDuplicate: true,
              duplicateContact: nameMatched,
              reason: softReason,
            });
            return;
          }
        }

        // No matches found
        setMatchState({ contact: null, level: null, reason: "" });
        onDuplicateChange?.({
          isHardDuplicate: false,
          isSoftDuplicate: false,
          duplicateContact: null,
          reason: "",
        });
      } catch (err) {
        console.error("Duplicate contact check error:", err);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [firstName, lastName, emailJsonb, linkedinUrl, currentContactId, dataProvider, onDuplicateChange]);

  if (!matchState.contact) return null;

  const { contact, level, reason } = matchState;
  const owner = salesList?.find((s) => s.id === contact.sales_id);
  const ownerName = owner ? `${owner.first_name} ${owner.last_name}` : "Unassigned";
  const isHard = level === "hard";

  return (
    <div
      className={`rounded-xl border p-4 my-3 text-sm shadow-sm animate-in fade-in slide-in-from-top-1 duration-200 ${
        isHard
          ? "border-red-500/40 bg-red-500/10 text-red-950 dark:text-red-100"
          : "border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg shrink-0 ${
            isHard
              ? "bg-red-500/20 text-red-600 dark:text-red-400"
              : "bg-amber-500/20 text-amber-600 dark:text-amber-400"
          }`}
        >
          {isHard ? <AlertCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-base tracking-tight">
              {isHard ? "Duplicate Contact Detected" : "Potential Duplicate Contact"}
            </h4>
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                isHard
                  ? "bg-red-500/20 text-red-700 dark:text-red-300"
                  : "bg-amber-500/20 text-amber-700 dark:text-amber-300"
              }`}
            >
              {isHard ? "Exact Match" : "Same Name"}
            </span>
          </div>

          <p
            className={`text-xs mt-0.5 ${
              isHard
                ? "text-red-800 dark:text-red-200/90 font-medium"
                : "text-amber-800 dark:text-amber-200/90"
            }`}
          >
            {reason}
          </p>

          <div
            className={`mt-2.5 p-2.5 rounded-lg bg-white/70 dark:bg-zinc-900/70 border grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs ${
              isHard
                ? "border-red-300/40 dark:border-red-700/40"
                : "border-amber-300/40 dark:border-amber-700/40"
            }`}
          >
            <div>
              <span className="text-muted-foreground block text-[11px]">Existing Contact</span>
              <span className="font-semibold flex items-center gap-1">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                {contact.first_name} {contact.last_name}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[11px]">Company & Title</span>
              <span className="font-medium flex items-center gap-1 text-foreground">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                {contact.title ? `${contact.title} at ` : ""}
                {contact.company_name || "No company"}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[11px]">Assigned Sales Owner</span>
              <span className="font-medium text-foreground">{ownerName}</span>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="default"
              className={`font-medium h-8 text-xs gap-1.5 shadow-sm text-white ${
                isHard
                  ? "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                  : "bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
              }`}
              onClick={() => navigate(`/contacts/${contact.id}/show`)}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Existing Contact Profile
            </Button>
            <span className="text-xs text-muted-foreground italic">
              {isHard
                ? "Creation blocked to protect team data integrity."
                : "Check to see if this is the same person before continuing."}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
